import { createTheme, ThemeProvider } from "@mui/material";
import * as React from "react";
import { useMount } from "react-use";
import { RecoilRoot } from "recoil";
import { RecoilUndoRoot } from "recoil-undo";
import { IXoraiaConfig, useXoraiaUpdater } from "../hooks/useXoraia";
import { generateBorder } from "../lib/generateBorder";
import { generateRadius } from "../lib/generateRadius";
import { generateSide } from "../lib/generateSide";
import { RecoilDevTools } from "../lib/RecoilDevTools";
import { MAIN_COLOR, SECONDARY_COLOR } from "../utils/constants/common/utils";
import { XGUIProps } from "../utils/interfaces/gui/fields.interfaces";
const XGUI = React.lazy(() => import("../components/gui/XGUI"));

const XoraiaProvider: React.FC<{
  children: React.ReactNode;
  initConfig?: IXoraiaConfig;
}> = ({
  children,
  initConfig = {
    businessId: "LOCALHOST",
    Link: (props) => <a {...props}>{props.children}</a>,
  },
}) => {
  return (
    <RecoilRoot override={false}>
      <RecoilUndoRoot>
        <RecoilDevTools />
        <Content initConfig={initConfig}>{children}</Content>
      </RecoilUndoRoot>
    </RecoilRoot>
  );
};

const Content: React.FC<{
  children: React.ReactNode;
  initConfig: IXoraiaConfig;
}> = ({ children, initConfig }) => {
  const setConfig = useXoraiaUpdater();

  useMount(() => {
    const _config: IXoraiaConfig = {
      env: "dev",
      theme: {},
      imageComponent: (props, env, devProps, index) => {
        const objectFit = props.objectFit ?? "cover";
        const {
          alt,
          bgColor,
          href,
          margin,
          padding,
          border,
          borderRadius,
          src,
          ...sx
        } = props;

        let image = (
          <img
            key={index}
            {...(env === "dev" && devProps)}
            alt={alt}
            src={src || "IMAGE_PLACEHOLDER"}
            style={{
              ...sx,
              padding: generateSide("padding", padding) as any,
              margin: generateSide("margin", margin) as any,
              objectFit,
              backgroundColor: bgColor,
              border: generateBorder(border),
              borderRadius: generateRadius(borderRadius),
            }}
            loading="lazy"
          />
        );

        // THIS WILL USE NEXT LINK IN PROD
        if (href && env !== "dev") {
          image = <a href={href}>{image}</a>;
        }

        return image;
      },
      ...initConfig,
    };

    if (_config.env === "dev") {
      _config.XGUI = (props: XGUIProps) => (
        <React.Suspense>
          <XGUI {...props} />
        </React.Suspense>
      );
    }

    setConfig(_config);
  });

  // if (!config.env || !initConfig.env) return <p>loading...</p>;

  const theme = createTheme({
    palette: {
      primary: { main: MAIN_COLOR },
      secondary: { main: SECONDARY_COLOR },
    },
  });

  return <ThemeProvider theme={theme}>{children}</ThemeProvider>;
};

export default XoraiaProvider;
